import { createContext, useContext, useState, useCallback, useEffect } from "react";
import type { Product, LigneDevis } from "@shared/schema";

export interface CartItem {
  product: Product;
  quantite: number;
}

interface DevisCartContextValue {
  items: CartItem[];
  addToCart: (product: Product, quantite?: number) => void;
  removeFromCart: (productId: string) => void;
  updateQuantite: (productId: string, quantite: number) => void;
  clearCart: () => void;
  toDevisLignes: () => LigneDevis[];
}

const DevisCartContext = createContext<DevisCartContextValue | null>(null);

export function DevisCartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const stored = localStorage.getItem("devis_cart_v2");
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  useEffect(() => {
    try { localStorage.setItem("devis_cart_v2", JSON.stringify(items)); } catch {}
  }, [items]);

  const addToCart = useCallback((product: Product, quantite = 1) => {
    setItems(prev => {
      const existing = prev.find(i => i.product.id === product.id);
      if (existing) {
        return prev.map(i => i.product.id === product.id ? { ...i, quantite: i.quantite + quantite } : i);
      }
      return [...prev, { product, quantite }];
    });
  }, []);

  const removeFromCart = useCallback((productId: string) => {
    setItems(prev => prev.filter(i => i.product.id !== productId));
  }, []);

  const updateQuantite = useCallback((productId: string, quantite: number) => {
    if (quantite < 1) return;
    setItems(prev => prev.map(i => i.product.id === productId ? { ...i, quantite } : i));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    try { localStorage.removeItem("devis_cart_v2"); } catch {}
  }, []);

  const toDevisLignes = useCallback((): LigneDevis[] =>
    items.map(({ product, quantite }) => ({
      id: crypto.randomUUID(),
      section: "produits" as const,
      produitId: product.id,
      imageUrl: product.imageUrl || undefined,
      sku: product.sku || undefined,
      nomFr: product.nomFr,
      descriptionFr: product.descriptionFr || "",
      puHt: Number(product.prixAffiche) || 0,
      quantite,
      remise: 0,
    })),
  [items]);

  return (
    <DevisCartContext.Provider value={{ items, addToCart, removeFromCart, updateQuantite, clearCart, toDevisLignes }}>
      {children}
    </DevisCartContext.Provider>
  );
}

export function useDevisCart() {
  const ctx = useContext(DevisCartContext);
  if (!ctx) throw new Error("useDevisCart must be used inside DevisCartProvider");
  return ctx;
}
